// shared-lib/millwork/miter.js
/**
 * How far a trim run overshoots an OUTSIDE corner so the two runs meet on the
 * corner's bisector: `depth · tan(deflection / 2)` — equal to `depth` only at a
 * square corner (reference-docs/architectural-trim/overview.md). Shared by the
 * wall trim (`ArchitecturalTrim.miterExtension`) and cabinet trim
 * (`shared-lib/cabinet-trim`), so the two mitre alike. Pure.
 */

/**
 * A joint whose deflection is below this is treated as a straight run:
 * the two segments are collinear, there is no corner, and no miter is cut.
 * Below ~1° the sign of the 2D cross product is dominated by scan noise
 * (a sub-millimetre kink flips it), so classifying such a joint as an
 * outside corner would extend and bevel trim on a visually flat wall.
 */
const MIN_MITER_DEFLECTION = (1 * Math.PI) / 180;

/**
 * Upper bound on miter extension, as a multiple of the profile's depth.
 * The exact extension is depth * tan(deflection / 2), which diverges as a
 * joint approaches a full doubling-back (180°). Real architecture stays
 * well under this — the sharpest outside corner in the reference scan
 * corpus is a 148° deflection, needing 3.49x depth — so the cap only ever
 * engages on degenerate scan geometry, where it bounds the spike instead
 * of emitting trim metres long.
 */
const MAX_MITER_EXTENSION_RATIO = 6; // reached at ~161° deflection

/**
 * @param {number} deflection - Turn angle at the joint, in radians
 * @param {number} profileDepth - How far the profile stands off its surface
 * @returns {number} Extension along the run, in the same units
 */
function miterExtension(deflection, profileDepth) {
  if (!(profileDepth > 0) || !(deflection > MIN_MITER_DEFLECTION)) return 0;
  const raw = profileDepth * Math.tan(deflection / 2);
  return Math.min(raw, profileDepth * MAX_MITER_EXTENSION_RATIO);
}

module.exports = {
  MIN_MITER_DEFLECTION,
  MAX_MITER_EXTENSION_RATIO,
  miterExtension,
};
